import {
    Box,
    IconButton,
    Menu,
    menuClasses,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Cog, X } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabeledValue, LabelIdValue } from '~/components/Fields/LabeledValue';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { formatReviewDateTime } from '~/utils/io/files/formatDate';
import StructureSetLabelField from '../StructureSetLabel/LabelField';
import ApprovalStatusDropdown from './ApprovalStatusDropdown';

// Simple component for displaying label-value pairs in a compact format
interface InfoItemProps {
    label: string;
    value: string | undefined | null;
}

const InfoItem = ({ label, value }: InfoItemProps) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr',
            gap: 0.1,
            mb: 0.5,
            alignItems: 'start',
        }}
    >
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            <FormattedMessage id={label} />
        </Typography>
        <Typography
            fontSize={12}
            sx={{
                wordBreak: 'break-word',
                textAlign: 'left',
            }}
        >
            {value && value.length !== 0 ? value : '-'}
        </Typography>
    </Box>
);

const DICOM_DESCRIPTION_MAX_LENGTH = 1024;

export const StructureSetSettings: React.FC = () => {
    const intl = useIntl();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const {
        structureSet,
        setStructureSetDescription,
        setStructureSetLabel,
        setStructureSetApprovalStatus,
        setRoisApprovalStatus,
    } = useStructureSet();

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDescriptionChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        event.stopPropagation();
        setStructureSetDescription(event.target.value);
    };

    const handleApprovalChange = (newApprovalStatus: DICOMApprovalStatus) => {
        setStructureSetApprovalStatus(newApprovalStatus);
        setRoisApprovalStatus(newApprovalStatus);
    };

    return (
        (structureSet && (
            <Box display="flex">
                <Tooltip
                    title={<FormattedMessage id="properties" />}
                    enterDelay={500}
                    enterNextDelay={500}
                >
                    <IconButton
                        color="primary"
                        size="small"
                        aria-controls="image-picker-menu"
                        aria-haspopup="true"
                        data-testid="structure-set-settings-button"
                        aria-label="structure-set-settings-button"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        sx={{ p: 1 }}
                    >
                        <Cog size={12} />
                    </IconButton>
                </Tooltip>
                <Menu
                    id="image-picker-menu"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    sx={{
                        width: 'calc(240px + 8px)',
                        ml: -0.5,
                        [`& .${menuClasses.paper}`]: {
                            width: '240px',
                            marginTop: '4px',
                            padding: 1.5,
                        },
                    }}
                    MenuListProps={{ dense: true }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        pb={1}
                        sx={{ ':focus-visible': { outline: 'none' } }}
                    >
                        <Typography fontWeight="medium">
                            <FormattedMessage id="properties" />
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            sx={{ p: 0.25 }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <Stack direction={'row'} gap={2}>
                            <LabelIdValue
                                label="SOP Instance UID"
                                id={structureSet.sopUid}
                            />
                            <LabeledValue
                                label="Structures"
                                value={structureSet.numberOfStructures}
                            />
                        </Stack>
                        <StructureSetLabelField
                            onChange={setStructureSetLabel}
                            structureSetLabel={structureSet.structureSetLabel}
                        />
                        <TextField
                            label="Description"
                            placeholder={intl.formatMessage({
                                id: 'structureSetDescription',
                            })}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            value={structureSet.structureSetDescription}
                            onChange={handleDescriptionChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                maxLength: DICOM_DESCRIPTION_MAX_LENGTH, // Structure set Description (3006,0006) 1024 chars maximum
                                style: {
                                    fontSize: 12,
                                    lineHeight: 1.25,
                                    paddingTop: 4,
                                },
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                            }}
                            data-testid="structure-set-description-field"
                        />
                        <ApprovalStatusDropdown
                            structureSet={structureSet}
                            onApprovalStatusChange={handleApprovalChange}
                        />
                        <Box>
                            <InfoItem
                                label="manufacturer"
                                value={structureSet.manufacturer}
                            />
                            <InfoItem
                                label="manufacturerModelName"
                                value={structureSet.manufacturerModelName}
                            />
                            <InfoItem
                                label="softwareVersions"
                                value={structureSet.softwareVersions}
                            />
                            <InfoItem
                                label="reviewerName"
                                value={structureSet.reviewerName}
                            />
                            <InfoItem
                                label="reviewTime"
                                value={formatReviewDateTime(
                                    structureSet.reviewDate,
                                    structureSet.reviewTime
                                )}
                            />
                        </Box>
                    </Box>
                </Menu>
            </Box>
        )) || <></>
    );
};